import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Bot, User, Send } from "lucide-react";

const ago = (iso) => { if (!iso) return ""; const m = Math.round((Date.now() - new Date(iso)) / 60000); return m < 1 ? "now" : m < 60 ? `${m}m` : m < 1440 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`; };

export default function ChatsTab({ initialSid }) {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(initialSid || null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [avail, setAvail] = useState(true);
  const endRef = useRef();

  const loadSessions = () => api.get("/admin/chats").then((r) => setSessions(r.data));
  const loadMsgs = (id) => api.get(`/admin/chats/${id}/messages`).then((r) => setMsgs(r.data));
  useEffect(() => { loadSessions(); api.get("/admin/settings").then((r) => setAvail(r.data.chat_availability?.available ?? true)); }, []);
  useEffect(() => {
    if (!sid) return;
    loadMsgs(sid);
    const t = setInterval(() => { loadMsgs(sid); loadSessions(); }, 5000);
    return () => clearInterval(t);
  }, [sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const current = sessions.find((s) => s.session_id === sid);
  const setMode = async (mode) => { await api.post(`/admin/chats/${sid}/mode`, { mode }); toast.success(mode === "ai" ? "AI replies resumed" : "AI paused — you're replying"); loadSessions(); };
  const reply = async (e) => {
    e.preventDefault();
    const t = text.trim(); if (!t) return;
    setText("");
    await api.post(`/admin/chats/${sid}/reply`, { text: t });
    loadMsgs(sid); loadSessions();
  };
  const toggleAvail = async () => {
    const v = !avail; setAvail(v);
    await api.put("/admin/settings/chat_availability", { value: { available: v } });
    toast.success(v ? "You're shown as available" : "Visitors will be asked to leave a message");
  };

  return (
    <div data-testid="chats-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-3xl font-bold">Chats</h1>
        <button onClick={toggleAvail} data-testid="chat-availability-toggle" className={`rounded-full px-4 py-2 text-sm font-semibold ${avail ? "bg-forest text-white" : "bg-cream-alt text-charcoal"}`}>{avail ? "Available for live chat" : "Unavailable — collecting messages"}</button>
      </div>
      <div className="mt-6 grid md:grid-cols-[280px_1fr] gap-4 min-w-0">
        <div className={`bg-white rounded-3xl shadow-card overflow-hidden ${sid ? "hidden md:block" : ""}`} data-testid="chat-sessions">
          {sessions.length === 0 && <div className="p-6 text-sm text-charcoal-muted">No conversations yet.</div>}
          {sessions.map((s) => (
            <button key={s.session_id} onClick={() => setSid(s.session_id)} data-testid={`chat-session-${s.session_id}`}
              className={`w-full text-left px-4 py-3 border-b border-cream-alt hover:bg-cream-alt/60 ${sid === s.session_id ? "bg-terracotta/10" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold truncate">{s.visitor_email || `Visitor ${s.session_id.slice(0, 6)}`}</span>
                <span className="text-[11px] text-charcoal-muted shrink-0">{ago(s.last_message_at)}</span>
              </div>
              <div className="text-xs text-charcoal-muted truncate mt-0.5">{s.last_text}</div>
              <div className="mt-1 flex gap-1.5">
                {s.needs_attention && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600">Needs you</span>}
                {s.mode === "human" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-charcoal text-cream">AI paused</span>}
                {s.unread && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-terracotta/15 text-terracotta">New</span>}
              </div>
            </button>
          ))}
        </div>
        {sid ? (
          <div className="bg-white rounded-3xl shadow-card flex flex-col h-[70vh] min-w-0 overflow-hidden" data-testid="chat-thread">
            <div className="px-4 py-3 border-b border-cream-alt flex items-center gap-2 flex-wrap">
              <button onClick={() => setSid(null)} className="md:hidden p-1 rounded-full hover:bg-cream-alt" aria-label="Back" data-testid="chat-back"><ArrowLeft size={18}/></button>
              <div className="flex-1 text-sm font-semibold truncate">{current?.visitor_email || `Visitor ${sid.slice(0, 6)}`}</div>
              {current?.mode === "human"
                ? <button onClick={() => setMode("ai")} data-testid="chat-resume-ai" className="text-xs rounded-full bg-cream-alt px-3 py-1.5 inline-flex items-center gap-1"><Bot size={12}/> Resume AI</button>
                : <button onClick={() => setMode("human")} data-testid="chat-take-over" className="text-xs rounded-full bg-charcoal text-cream px-3 py-1.5 inline-flex items-center gap-1"><User size={12}/> Take over</button>}
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 text-sm" data-testid="chat-thread-messages">
              {msgs.map((m, i) => (
                <div key={i} className={`max-w-[80%] break-words rounded-2xl px-3 py-2 ${m.role === "user" ? "bg-cream-alt" : m.role === "admin" ? "ml-auto bg-terracotta text-white" : "ml-auto bg-charcoal/85 text-cream"}`}>
                  <div className="text-[10px] opacity-70 mb-0.5">{m.role === "user" ? "Visitor" : m.role === "admin" ? "You" : "AI"}</div>{m.text}
                </div>
              ))}
              <div ref={endRef}/>
            </div>
            <form onSubmit={reply} className="p-3 border-t border-cream-alt flex gap-2">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply as the studio…" data-testid="chat-admin-input" className="flex-1 rounded-full border border-cream-alt px-4 py-2.5 text-sm"/>
              <button type="submit" disabled={!text.trim()} data-testid="chat-admin-send" className="w-10 h-10 rounded-full bg-terracotta text-white grid place-items-center disabled:opacity-40"><Send size={16}/></button>
            </form>
          </div>
        ) : <div className="hidden md:grid place-items-center text-sm text-charcoal-muted bg-white rounded-3xl shadow-card">Select a conversation</div>}
      </div>
    </div>
  );
}
